import { BOW_OFFSET_PX, TRAIL_LIMIT, TURN_TAX } from './constants.ts';
import {
  add,
  clamp,
  dot,
  fromAngle,
  headingUnit,
  length,
  lengthSq,
  scale,
  sub,
  toAngle,
} from './math.ts';
import { bowPosition, solveTurnArc } from './movement.ts';
import type { BoatState, Vec2 } from './types.ts';

/**
 * Candidate shapes for the path a boat sweeps while completing one move.
 * They differ in which tangents they honour: the heading the boat leaves on,
 * the heading it adopts on landing, or neither.
 */
export type CurveKind =
  'straight' | 'cubicVelocity' | 'cubicEven' | 'quadraticCenter' | 'arc';

export const CURVE_KINDS: ReadonlyArray<{ kind: CurveKind; label: string }> = [
  { kind: 'straight', label: 'Straight' },
  { kind: 'cubicVelocity', label: 'Cubic (velocity)' },
  { kind: 'cubicEven', label: 'Cubic (even)' },
  { kind: 'quadraticCenter', label: 'Quadratic (center)' },
  { kind: 'arc', label: 'Arc' },
];

/** The arc is the path the simulation actually solves, not an approximation. */
export const DEFAULT_CURVE_KIND: CurveKind = 'arc';

export type CubicSegment = {
  p0: Vec2;
  p1: Vec2;
  p2: Vec2;
  p3: Vec2;
};

export type MovePath = {
  kind: CurveKind;
  segments: CubicSegment[];
};

type MoveGeometry = {
  from: Vec2;
  to: Vec2;
  distance: number;
  /** Unit direction the boat is already travelling in. */
  departure: Vec2;
  /** Unit direction the boat adopts on landing, from the turn solver. */
  arrival: Vec2;
  /** Current velocity, which is also the offset to the movement circle center. */
  entryVelocity: Vec2;
  /** Velocity the boat holds on landing: the exit tangent scaled by kept speed. */
  exitVelocity: Vec2;
};

const EPSILON = 1e-6;
const MAX_ARC_SWEEP = Math.PI / 2;

export function projectMovePath(
  boat: BoatState,
  destination: Vec2,
  kind: CurveKind = DEFAULT_CURVE_KIND,
  tax: number = TURN_TAX,
): MovePath | null {
  const arc = solveTurnArc(boat, destination, tax);

  if (!arc) {
    return null;
  }

  const arrival = fromAngle(arc.heading);
  const geometry: MoveGeometry = {
    from: { ...boat.position },
    to: { ...destination },
    distance: length(sub(destination, boat.position)),
    departure: fromAngle(boat.heading),
    arrival,
    entryVelocity: fromAngle(boat.heading, boat.speed),
    exitVelocity: fromAngle(arc.heading, arc.speed),
  };
  const segments = buildSegments(kind, geometry);

  return {
    kind,
    segments:
      segments.length > 0
        ? segments
        : [lineSegment(geometry.from, geometry.to)],
  };
}

/**
 * Hover chrome: the commit path with the prefix still aft of the bow removed.
 * Dest on the hull (or otherwise aft of the bow) leaves nothing to draw — that
 * is the path the hull would swim, not a second turn solved from the nose.
 */
export function clipMovePathAheadOfBow(
  path: MovePath,
  boat: BoatState,
  bowOffsetPx: number = BOW_OFFSET_PX,
): Vec2[] {
  const points = sampleMovePath(path);
  const dest = points.at(-1);

  if (!dest || points.length < 2 || alongBow(dest, boat, bowOffsetPx) <= EPSILON) {
    return [];
  }

  let index = 0;

  while (index < points.length && alongBow(points[index]!, boat, bowOffsetPx) <= EPSILON) {
    index += 1;
  }

  if (index >= points.length) {
    return [];
  }

  if (index === 0) {
    return points;
  }

  const previous = points[index - 1]!;
  const current = points[index]!;
  const startAlong = alongBow(previous, boat, bowOffsetPx);
  const endAlong = alongBow(current, boat, bowOffsetPx);
  const span = endAlong - startAlong;
  const t = Math.abs(span) < EPSILON ? 1 : clamp(-startAlong / span, 0, 1);
  const start = add(previous, scale(sub(current, previous), t));

  return [start, ...points.slice(index)];
}

const LAND_CUT_STEPS = 8;

function shorelineOnEdge(
  from: Vec2,
  to: Vec2,
  fromLand: boolean,
  isLand: (point: Vec2) => boolean,
): Vec2 {
  let water = fromLand ? 1 : 0;
  let land = fromLand ? 0 : 1;

  for (let step = 0; step < LAND_CUT_STEPS; step += 1) {
    const mid = (water + land) / 2;
    const point = add(from, scale(sub(to, from), mid));

    if (isLand(point)) {
      land = mid;
    } else {
      water = mid;
    }
  }

  return add(from, scale(sub(to, from), water));
}

function flushRun(runs: Vec2[][], run: Vec2[]): void {
  if (run.length >= 2) {
    runs.push(run);
  }
}

/**
 * Split a hover polyline into open (solid) and masked (dashed under a cap) runs.
 * Vertices stay in path order so a dash can walk a hidden run.
 */
export function splitPolylineByLand(
  points: readonly Vec2[],
  isLand: (point: Vec2) => boolean,
): { water: Vec2[][]; land: Vec2[][] } {
  const water: Vec2[][] = [];
  const land: Vec2[][] = [];

  if (points.length < 2) {
    return { water, land };
  }

  const first = points[0]!;
  let onLand = isLand(first);
  let run: Vec2[] = [first];

  for (let i = 1; i < points.length; i += 1) {
    const next = points[i]!;
    const nextLand = isLand(next);

    if (nextLand === onLand) {
      run.push(next);
      continue;
    }

    const cut = shorelineOnEdge(run[run.length - 1]!, next, onLand, isLand);
    run.push(cut);
    flushRun(onLand ? land : water, run);
    onLand = nextLand;
    run = [cut, next];
  }

  flushRun(onLand ? land : water, run);

  return { water, land };
}

/** Signed distance past the bow, along heading. Positive is ahead of the nose. */
function alongBow(point: Vec2, boat: BoatState, bowOffsetPx: number = BOW_OFFSET_PX): number {
  return dot(sub(point, bowPosition(boat, bowOffsetPx)), headingUnit(boat.heading));
}

/**
 * Flattens a `MovePath` into a polyline, `samplesPerSegment` points per cubic segment (plus the
 * final endpoint). Not arc-length uniform — good enough for geometric tests like checkpoint
 * crossing, not for animation.
 */
export function sampleMovePath(path: MovePath, samplesPerSegment = 16): Vec2[] {
  const points: Vec2[] = [];

  for (const segment of path.segments) {
    for (let s = 0; s < samplesPerSegment; s += 1) {
      points.push(cubicBezierPoint(segment, s / samplesPerSegment));
    }
  }

  const last = path.segments.at(-1);

  if (last) {
    points.push({ ...last.p3 });
  }

  return points;
}

/**
 * Keeps the prefix of a polyline up to `progress` of its total length. Same parameter the boat
 * tween feeds Phaser's `Path.getPoint`, so a travelled-wake or a just-crossed gate can share it.
 */
export function polylineLength(points: readonly Vec2[]): number {
  let total = 0;

  for (let i = 1; i < points.length; i += 1) {
    const from = points[i - 1];
    const to = points[i];

    if (from && to) {
      total += length(sub(to, from));
    }
  }

  return total;
}

/**
 * Arc-length lookup over a flattened path. `sampleMovePath` walks the cubic
 * parameter, so its points bunch where the curve is tight; the hull tween rides
 * Phaser's arc-length uniform `Path.getPoint`. Anything that has to agree with
 * what the player watches — a boat-versus-boat sweep above all — reads the table,
 * not the raw samples.
 */
export type PathArcTable = {
  points: Vec2[];
  /** Running length at each point. First entry is 0; same length as `points`. */
  cumulative: number[];
  total: number;
};

export function arcLengthTable(path: MovePath, samplesPerSegment = 16): PathArcTable {
  const points = sampleMovePath(path, samplesPerSegment);
  const cumulative: number[] = [0];

  for (let i = 1; i < points.length; i += 1) {
    cumulative.push(cumulative[i - 1]! + length(sub(points[i]!, points[i - 1]!)));
  }

  return { points, cumulative, total: cumulative.at(-1) ?? 0 };
}

/** A standing boat as a table, so the sweep does not special-case a seat that is not moving. */
export function restingArcTable(position: Vec2): PathArcTable {
  return { points: [{ ...position }], cumulative: [0], total: 0 };
}

/**
 * Arc-length fraction of `path` nearest `point`. The contact sits on the hull
 * skin, not on the centreline, so the scene uses this to learn when the moving
 * picture reaches the blow.
 */
export function progressNearestPoint(path: MovePath, point: Vec2): number {
  const table = arcLengthTable(path);

  if (table.points.length === 0 || table.total < EPSILON) {
    return 0;
  }

  let best = 0;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (let index = 0; index < table.points.length; index += 1) {
    const distance = lengthSq(sub(table.points[index]!, point));

    if (distance < bestDistance) {
      bestDistance = distance;
      best = table.cumulative[index]! / table.total;
    }
  }

  return best;
}

/** Point at `progress` of total length, clamped to both ends. */
export function pointAtProgress(table: PathArcTable, progress: number): Vec2 {
  const first = table.points[0];

  if (!first) {
    return { x: 0, y: 0 };
  }

  if (table.total < EPSILON || table.points.length < 2) {
    return { ...first };
  }

  const found = locateProgress(table, progress);
  const from = table.points[found.index]!;
  const to = table.points[found.index + 1]!;

  return add(from, scale(sub(to, from), found.t));
}

/**
 * Travel direction at `progress`. Walks outward to the first pair of distinct
 * points, so a repeated sample in a tight fillet does not read as a zero tangent.
 */
export function headingAtProgress(table: PathArcTable, progress: number): number | null {
  if (table.points.length < 2 || table.total < EPSILON) {
    return null;
  }

  const { index } = locateProgress(table, progress);

  for (let span = index; span >= 0; span -= 1) {
    const from = table.points[span];
    const to = table.points[span + 1];

    if (from && to && lengthSq(sub(to, from)) > EPSILON * EPSILON) {
      return toAngle(sub(to, from));
    }
  }

  return null;
}

/** Index of the span holding `progress`, and how far into it. */
function locateProgress(
  table: PathArcTable,
  progress: number,
): { index: number; t: number } {
  const last = table.points.length - 2;
  const target = clamp(progress, 0, 1) * table.total;
  let low = 0;
  let high = last;

  while (low < high) {
    const mid = (low + high) >> 1;

    if (table.cumulative[mid + 1]! < target) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  const start = table.cumulative[low]!;
  const span = table.cumulative[low + 1]! - start;

  return { index: low, t: span < EPSILON ? 0 : clamp((target - start) / span, 0, 1) };
}

/**
 * Drop `distance` from the end so a bounce fillet can start before the shore.
 * Splits the last cubic in place rather than re-flattening the whole run, so a
 * chain of bounces cannot multiply the segment count.
 */
export function trimMovePathEnd(path: MovePath, distance: number): MovePath | null {
  if (distance <= EPSILON) {
    return path;
  }

  const segments = [...path.segments];
  let remaining = distance;

  while (segments.length > 0) {
    const last = segments.at(-1)!;
    const span = segmentLength(last);

    if (span <= remaining + EPSILON) {
      segments.pop();
      remaining -= span;
      continue;
    }

    segments[segments.length - 1] = cubicHead(last, (span - remaining) / span);

    return { kind: path.kind, segments };
  }

  return null;
}

/** Join cubic runs so a crash can be inbound + fillet + outbound in one tween. */
export function concatMovePaths(
  kind: CurveKind,
  parts: readonly MovePath[],
): MovePath | null {
  const segments: CubicSegment[] = [];

  for (const part of parts) {
    segments.push(...part.segments);
  }

  return segments.length === 0 ? null : { kind, segments };
}

/** Circular cubics that leave `from` on `heading` and pass through `to`. */
export function circularArcSegments(
  from: Vec2,
  heading: number,
  to: Vec2,
): CubicSegment[] {
  return arcSegments(from, fromAngle(heading), to);
}

/** Exit tangent of the last cubic, or null when the path is empty. */
export function pathEndHeading(path: MovePath): number | null {
  const last = path.segments.at(-1);

  if (!last) {
    return null;
  }

  const handle = sub(last.p3, last.p2);

  if (lengthSq(handle) > EPSILON * EPSILON) {
    return toAngle(handle);
  }

  const span = sub(last.p3, last.p0);

  return lengthSq(span) > EPSILON * EPSILON ? toAngle(span) : null;
}

/** A polyline as cubic line-segments, for a clipped crash path the hull can tween. */
export function pathFromPolyline(
  points: readonly Vec2[],
  kind: CurveKind = DEFAULT_CURVE_KIND,
): MovePath | null {
  if (points.length < 2) {
    return null;
  }

  const segments: CubicSegment[] = [];

  for (let i = 1; i < points.length; i += 1) {
    const from = points[i - 1];
    const to = points[i];

    if (from && to) {
      segments.push(lineSegment(from, to));
    }
  }

  if (segments.length === 0) {
    return null;
  }

  return { kind, segments };
}

export function clipPolylineToProgress(points: readonly Vec2[], progress: number): Vec2[] {
  if (points.length === 0) {
    return [];
  }

  const amount = clamp(progress, 0, 1);

  if (amount >= 1) {
    return [...points];
  }

  if (amount <= 0) {
    return [{ ...points[0]! }];
  }

  const cumulative = [0];

  for (let i = 1; i < points.length; i += 1) {
    cumulative.push(cumulative[i - 1]! + length(sub(points[i]!, points[i - 1]!)));
  }

  const total = cumulative.at(-1)!;

  if (total < EPSILON) {
    return [{ ...points[0]! }];
  }

  const target = amount * total;
  const clipped: Vec2[] = [{ ...points[0]! }];

  for (let i = 1; i < points.length; i += 1) {
    const end = cumulative[i]!;

    if (end < target) {
      clipped.push({ ...points[i]! });
      continue;
    }

    const start = cumulative[i - 1]!;
    const span = end - start;
    const t = span < EPSILON ? 1 : (target - start) / span;
    clipped.push(add(points[i - 1]!, scale(sub(points[i]!, points[i - 1]!), t)));
    break;
  }

  return clipped;
}

function segmentLength(segment: CubicSegment, samples = 16): number {
  const points: Vec2[] = [];

  for (let s = 0; s <= samples; s += 1) {
    points.push(cubicBezierPoint(segment, s / samples));
  }

  return polylineLength(points);
}

/** de Casteljau: the `[0, t]` part of a cubic, as a cubic. */
function cubicHead(segment: CubicSegment, t: number): CubicSegment {
  const lerp = (a: Vec2, b: Vec2): Vec2 => add(a, scale(sub(b, a), t));
  const p01 = lerp(segment.p0, segment.p1);
  const p12 = lerp(segment.p1, segment.p2);
  const p23 = lerp(segment.p2, segment.p3);
  const p012 = lerp(p01, p12);
  const p123 = lerp(p12, p23);

  return { p0: { ...segment.p0 }, p1: p01, p2: p012, p3: lerp(p012, p123) };
}

function cubicBezierPoint(segment: CubicSegment, t: number): Vec2 {
  const inverse = 1 - t;
  const w0 = inverse ** 3;
  const w1 = 3 * inverse ** 2 * t;
  const w2 = 3 * inverse * t ** 2;
  const w3 = t ** 3;

  return {
    x: w0 * segment.p0.x + w1 * segment.p1.x + w2 * segment.p2.x + w3 * segment.p3.x,
    y: w0 * segment.p0.y + w1 * segment.p1.y + w2 * segment.p2.y + w3 * segment.p3.y,
  };
}

export function pushTrail(
  trail: readonly MovePath[],
  path: MovePath,
  limit: number = TRAIL_LIMIT,
): MovePath[] {
  if (limit <= 0) {
    return [];
  }

  return [...trail, path].slice(-limit);
}

function buildSegments(
  kind: CurveKind,
  geometry: MoveGeometry,
): CubicSegment[] {
  switch (kind) {
    case 'straight':
      return [lineSegment(geometry.from, geometry.to)];
    case 'cubicEven':
      return [cubicEven(geometry)];
    case 'quadraticCenter':
      return [quadraticCenter(geometry)];
    case 'arc':
      return arcSegments(geometry.from, geometry.departure, geometry.to);
    case 'cubicVelocity':
      return [cubicVelocity(geometry)];
  }
}

/**
 * A Hermite of the two velocities: handles are a third of the velocity the boat
 * carries in and a third of the one it leaves with. A slow boat barely bends
 * out of the turn; a fast one swings wide.
 */
function cubicVelocity(geometry: MoveGeometry): CubicSegment {
  return {
    p0: { ...geometry.from },
    p1: add(geometry.from, scale(geometry.entryVelocity, 1 / 3)),
    p2: sub(geometry.to, scale(geometry.exitVelocity, 1 / 3)),
    p3: { ...geometry.to },
  };
}

/**
 * The same two tangents as `cubicVelocity`, but both handles are a third of the
 * move distance, so the bend no longer depends on how fast the boat is going.
 */
function cubicEven(geometry: MoveGeometry): CubicSegment {
  const handle = geometry.distance / 3;

  return {
    p0: { ...geometry.from },
    p1: add(geometry.from, scale(geometry.departure, handle)),
    p2: sub(geometry.to, scale(geometry.arrival, handle)),
    p3: { ...geometry.to },
  };
}

/**
 * A quadratic pulled toward the movement circle center, raised to cubic form.
 * It leaves along the current velocity but arrives along the center-to-target
 * direction, so its end tangent does not match the boat's new heading.
 */
function quadraticCenter(geometry: MoveGeometry): CubicSegment {
  const control = add(geometry.from, geometry.entryVelocity);

  return {
    p0: { ...geometry.from },
    p1: add(geometry.from, scale(sub(control, geometry.from), 2 / 3)),
    p2: add(geometry.to, scale(sub(control, geometry.to), 2 / 3)),
    p3: { ...geometry.to },
  };
}

/**
 * The single circular arc that leaves `from` along `tangent` and passes through
 * `to`. This is the shape the turn solver describes, so its end tangent is the
 * heading the boat adopts and its length is the boat's new speed.
 */
function arcSegments(from: Vec2, tangent: Vec2, to: Vec2): CubicSegment[] {
  const chord = sub(to, from);
  const span = length(chord);

  if (span === 0) {
    return [];
  }

  const normal = { x: -tangent.y, y: tangent.x };
  const offset = dot(normal, chord);

  if (Math.abs(offset) < EPSILON * span) {
    return [lineSegment(from, to)];
  }

  const signedRadius = lengthSq(chord) / (2 * offset);
  const center = add(from, scale(normal, signedRadius));
  const radius = Math.abs(signedRadius);
  const start = toAngle(sub(from, center));
  const sweep = arcSweep(start, toAngle(sub(to, center)), signedRadius > 0);
  const count = Math.max(1, Math.ceil(Math.abs(sweep) / MAX_ARC_SWEEP));
  const step = sweep / count;
  const handle = (4 / 3) * Math.tan(step / 4) * radius;
  const segments: CubicSegment[] = [];

  for (let index = 0; index < count; index += 1) {
    const a0 = start + step * index;
    const a1 = a0 + step;
    const p0 = onCircle(center, radius, a0);
    const p3 = onCircle(center, radius, a1);

    segments.push({
      p0,
      p1: add(p0, scale(circleTangent(a0), handle)),
      p2: sub(p3, scale(circleTangent(a1), handle)),
      p3,
    });
  }

  return segments;
}

function arcSweep(
  start: number,
  end: number,
  counterClockwise: boolean,
): number {
  const full = Math.PI * 2;
  let sweep = (end - start) % full;

  if (counterClockwise && sweep <= 0) {
    sweep += full;
  }

  if (!counterClockwise && sweep >= 0) {
    sweep -= full;
  }

  return sweep;
}

function onCircle(center: Vec2, radius: number, angle: number): Vec2 {
  return {
    x: center.x + Math.cos(angle) * radius,
    y: center.y + Math.sin(angle) * radius,
  };
}

function circleTangent(angle: number): Vec2 {
  return { x: -Math.sin(angle), y: Math.cos(angle) };
}

function lineSegment(from: Vec2, to: Vec2): CubicSegment {
  const step = scale(sub(to, from), 1 / 3);

  return {
    p0: { ...from },
    p1: add(from, step),
    p2: sub(to, step),
    p3: { ...to },
  };
}
